/* BulkFixModal — actionable "fix what's missing" workflow.
 *
 * Opens scoped to ONE field (or "any critical gap"). Lists the products
 * missing that field with an inline input for the missing value. Save
 * patches all rows in parallel via PATCH /products/{id}.
 *
 * Replaces the dead-end "?gap=<field> filtered catalog" pattern with a
 * single-screen bulk fix. Same look-and-feel as ImportPreviewModal /
 * BulkEditModal so sellers don't have to learn a third pattern.
 *
 * Props:
 *   onClose       — required, dismisses the modal
 *   gap?          — { field, label } restrict to one gap
 *   onSaved       — called after save with the count fixed
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  AlertTriangle, Save, X, ExternalLink, Image as ImageIcon, Loader2, ArrowRight,
} from "lucide-react";

const FIELD_LABELS = {
  image:        { label: "Image URL",        placeholder: "https://…/photo.jpg" },
  category:     { label: "Category",         placeholder: "e.g. Skincare" },
  description:  { label: "Description",      placeholder: "Short product description" },
  brand:        { label: "Brand",            placeholder: "Brand name" },
  retail_price: { label: "Retail (MSRP)",    placeholder: "e.g. 42.00", type: "number" },
  moq:          { label: "MOQ",              placeholder: "e.g. 6",    type: "number" },
};

export default function BulkFixModal({ gap, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({});            // { product_id: { field: newValue } }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Helper — does this product need fixing on `gap.field`?
  const isGapOnProduct = (p, field) => {
    if (field === "moq")   return !p.moq;
    if (field === "stock") return !p.stock;
    if (field?.startsWith("custom:")) {
      const k = field.slice(7);
      return !((p.custom_fields || {})[k]);
    }
    const v = p[field];
    return v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/products");
        const all = r.data || [];
        const field = gap?.field || "image";   // default — most common missing field
        const filtered = all.filter(p => isGapOnProduct(p, field));
        setProducts(filtered);
      } catch (e) {
        toast.error("Could not load products");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [gap?.field]);

  const fieldKey = gap?.field || "image";
  const meta = FIELD_LABELS[fieldKey] || { label: fieldKey, placeholder: "" };

  const setVal = (pid, v) => setEdits(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [fieldKey]: v } }));

  const filledCount = useMemo(
    () => Object.values(edits).filter(p => (p[fieldKey] ?? "") !== "").length,
    [edits, fieldKey]
  );

  const save = async () => {
    setBusy(true);
    const patches = Object.entries(edits)
      .filter(([_, fields]) => fields[fieldKey] !== undefined && fields[fieldKey] !== "")
      .map(([pid, fields]) => {
        const v = fields[fieldKey];
        const coerced = meta.type === "number" ? parseFloat(v) : v;
        return { pid, patch: { [fieldKey]: coerced } };
      });
    if (patches.length === 0) {
      toast.info("Nothing to save");
      setBusy(false);
      return;
    }
    try {
      // PATCH each product in parallel — fast for small N (typically <50)
      const results = await Promise.allSettled(
        patches.map(({ pid, patch }) =>
          api.patch(`/products/${pid}`, patch)
        )
      );
      const ok = results.filter(r => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`Updated ${ok} product${ok !== 1 ? "s" : ""}${failed > 0 ? ` · ${failed} failed` : ""}`);
      if (failed > 0 && ok === 0) toast.error("All saves failed — refresh and try again");
      onSaved && onSaved(ok);
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="bulk-fix-modal">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600"/>
            Fix missing {meta.label.toLowerCase()}
          </DialogTitle>
          <DialogDescription className="text-foreground/55">
            {products.length} product{products.length !== 1 ? "s" : ""} need {meta.label.toLowerCase()}.
            Fill in any (or all) below and save — products without a value stay as-is.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 mt-2 mb-3 flex items-center gap-2 text-[12px] text-foreground/65 flex-wrap">
          {filledCount > 0 && (
            <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px]">
              {filledCount} ready to save
            </span>
          )}
          {fieldKey === "image" && (
            <span className="text-foreground/55 inline-flex items-center gap-1">
              <ImageIcon className="w-3 h-3"/>Tip: for bulk image upload, close this and use the <strong>Bulk image upload</strong> tile on the import page.
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          {loading ? (
            <div className="py-16 text-center text-foreground/45"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center text-foreground/55 text-sm">
              <div className="text-2xl mb-2">🎉</div>
              All products have a {meta.label.toLowerCase()}. Nothing to fix here.
            </div>
          ) : (
            <div className="space-y-2 pb-3">
              {products.map(p => (
                <div key={p.id} className="rounded-xl border border-border bg-white px-3 py-2.5 flex items-center gap-3" data-testid={`fix-row-${p.sku}`}>
                  <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden shrink-0 grid place-items-center text-foreground/30">
                    {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover"/> : <ImageIcon className="w-5 h-5"/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="text-[10.5px] mono text-foreground/55 mt-0.5">{p.sku}</div>
                  </div>
                  <input
                    type={meta.type === "number" ? "number" : "text"}
                    value={(edits[p.id] && edits[p.id][fieldKey]) ?? ""}
                    onChange={(e) => setVal(p.id, e.target.value)}
                    placeholder={meta.placeholder}
                    data-testid={`fix-input-${p.sku}`}
                    className="w-80 h-10 rounded-md border border-input bg-white px-3 text-[12.5px] focus:outline-none focus:border-foreground/40 shrink-0"/>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-white px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || filledCount === 0} data-testid="bulk-fix-save"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            <Save className="w-3.5 h-3.5 mr-1.5"/>
            {busy ? "Saving…" : `Save ${filledCount} fix${filledCount !== 1 ? "es" : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
